import React, { useState, useEffect } from 'react';
import { useLoading } from '../contexts/LoadingContext';
import { useError } from '../contexts/ErrorContext';
import { useAuth } from '../contexts/AuthContext';

interface CheckInOutRecord {
  id: number;
  staff_id: string;
  check_type: 'check_in' | 'check_out';
  check_time: string;
  location_latitude: string;
  location_longitude: string;
  location_address: string;
  created_at: string;
  staff_name: string;
  staff_role?: string;
  staff_color?: string;
}

interface StaffSalaryInfo {
  staffId: string;
  staffName: string;
  staffRole: string | undefined;
  staffColor: string | undefined;
  basicSalary: number;
  extraSalary: number;
  totalShifts: number;
  totalHours: number;
  totalSalary: number;
  totalExtraHours: number;
  holidayDetails?: Array<{
    date: string;
    hours: number;
    extraHours: number;
    multiplier: number;
    isHoliday: boolean;
    holidayName?: string;
    daySalary: number;
  }>;
}

const ROLE_OPTIONS = [
  { value: 'CSKH', label: 'Chăm sóc khách hàng' },
  { value: 'SX_AO', label: 'Sản xuất áo' },
  { value: 'SX_THE', label: 'Sản xuất thẻ' },
];

// Helper function to get role label from value
const getRoleLabel = (value: string | undefined): string => {
  if (!value) return '-';
  const option = ROLE_OPTIONS.find(opt => opt.value === value);
  return option ? option.label : value;
};

// Helper function to convert ISO datetime (YYYY-MM-DDTHH:mm) to DD-MM-YYYY HH:mm
const formatDateTime = (dateString: string): string => {
  if (!dateString) return '';
  try {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes} ${day}-${month} ${year === currentYear ? '' : year}`;
  } catch {
    return '';
  }
};

// Helper function to get default month/year
const getDefaultMonthYear = () => {
  const now = new Date();
  return {
    month: String(now.getMonth() + 1).padStart(2, '0'),
    year: String(now.getFullYear())
  };
};

// Helper function to get date range from month/year
const getDateRangeFromMonthYear = (month: string, year: string) => {
  const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
  const endDate = new Date(parseInt(year), parseInt(month), 0); // Last day of month
  
  const formatDate = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  
  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate)
  };
};

const getTextColor = (bgColor: string | undefined): string => {
  if (!bgColor) return '#333';
  const hex = bgColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#333' : '#fff';
};

const formatCurrency = (value: number | string | undefined): string => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return '0';
  return Math.round(amount).toLocaleString('vi-VN', { maximumFractionDigits: 0 });
};

export const SalaryInfoPage: React.FC = () => {
  const { setLoading } = useLoading();
  const { error, setError, clearError } = useError();
  const { user } = useAuth();
  
  const defaultMonthYear = getDefaultMonthYear();
  const [selectedMonth, setSelectedMonth] = useState(defaultMonthYear.month);
  const [selectedYear, setSelectedYear] = useState(defaultMonthYear.year);
  const [records, setRecords] = useState<CheckInOutRecord[]>([]);
  const [salaryInfo, setSalaryInfo] = useState<StaffSalaryInfo | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [selectedMonth, selectedYear, user?.id]);

  const loadData = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true, 'Đang tải dữ liệu...');
      clearError();
      
      const dateRange = getDateRangeFromMonthYear(selectedMonth, selectedYear);
      const queryParams = new URLSearchParams({
        month: selectedMonth,
        year: selectedYear,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      
      const response = await fetch(`/api/salary/my-info?${queryParams.toString()}`);
      
      const data = await response.json();
      
      if (data.success) {
        setRecords(data.data.records || []);
        setSalaryInfo(data.data.salaryInfo || null);
      } else {
        setError(data.error || 'Lỗi khi tải dữ liệu');
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Lỗi khi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = () => {
    const newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    setSortOrder(newOrder);
    
    const sorted = [...records].sort((a, b) => {
      const dateA = new Date(a.check_time).getTime();
      const dateB = new Date(b.check_time).getTime();
      return newOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });
    
    setRecords(sorted);
  };

  // Generate month options (1-12)
  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const month = String(i + 1).padStart(2, '0');
    return { value: month, label: `Tháng ${i + 1}` };
  });

  // Generate year options (current year and previous 2 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 3 }, (_, i) => {
    const year = currentYear - i;
    return { value: String(year), label: String(year) };
  });

  return (
    <div className="salary-info-page" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div className="container">
        <div className="header" style={{ marginBottom: '20px' }}>
          <h1><i className="fas fa-money-bill-wave"></i> Thông tin lương</h1>
        </div>

        {/* Month/Year Selector */}
        <div style={{ 
          background: 'white', 
          padding: '20px', 
          borderRadius: '8px', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '20px',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                Tháng:
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '8px', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                {monthOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                Năm:
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '8px', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                {yearOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '12px',
            background: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            marginBottom: '20px',
            color: '#721c24'
          }}>
            <i className="fas fa-exclamation-circle"></i> {error}
          </div>
        )}

        {/* Salary Info Card */}
        {salaryInfo && (
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginBottom: '20px',
            border: `2px solid ${salaryInfo.staffColor || '#e2e8f0'}`
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '20px',
              borderBottom: '1px solid #e2e8f0'
            }}>
              <div
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '8px',
                  background: salaryInfo.staffColor || '#e9ecef',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: getTextColor(salaryInfo.staffColor),
                  fontWeight: '600',
                  fontSize: '20px',
                  marginRight: '15px'
                }}
              >
                {salaryInfo.staffName.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', fontSize: '18px', color: '#1e293b' }}>
                  {salaryInfo.staffName}
                </div>
                {salaryInfo.staffRole && (
                  <div style={{ fontSize: '14px', color: '#64748b', marginTop: '4px' }}>
                    {getRoleLabel(salaryInfo.staffRole)}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                  Giờ làm
                </div>
                <div style={{ fontSize: '24px', fontWeight: '600', color: '#10b981' }}>
                  {salaryInfo.totalHours}h
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                  Tăng ca
                </div>
                <div style={{ fontSize: '24px', fontWeight: '600', color: '#10b981' }}>
                  {salaryInfo.totalExtraHours}h
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                  Số ca làm
                </div>
                <div style={{ fontSize: '24px', fontWeight: '600', color: '#8d2a2c' }}>
                  {salaryInfo.totalShifts}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                  Lương cơ bản/giờ
                </div>
                <div style={{ fontSize: '16px', fontWeight: '500', color: '#475569' }}>
                  {formatCurrency(salaryInfo.basicSalary)} đ
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                  Phụ cấp tăng ca/giờ
                </div>
                <div style={{ fontSize: '16px', fontWeight: '500', color: '#475569' }}>
                  {formatCurrency(salaryInfo.extraSalary)} đ
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                  Tổng lương
                </div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#f59e0b' }}>
                  {formatCurrency(salaryInfo.totalSalary)} đ
                </div>
              </div>
            </div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e2e8f0' }}>
              Lưu ý: Tổng lương = Lương cơ bản × Tổng giờ làm + Phụ cấp tăng ca × Tổng giờ tăng ca (áp dụng nhân hệ số đối với giờ làm ngày lễ).
            </div>
            {salaryInfo.holidayDetails && salaryInfo.holidayDetails.length > 0 && (
              <div style={{ marginTop: '15px', padding: '15px', background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '6px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#b45309', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <i className="fas fa-gift"></i> Ngày lễ đã làm (được nhân hệ số):
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {salaryInfo.holidayDetails.map((detail, idx) => {
                    const d = new Date(detail.date);
                    const dateFormatted = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
                    return (
                      <div key={idx} style={{ fontSize: '13px', color: '#78350f', display: 'flex', justifyContent: 'space-between', borderBottom: idx < salaryInfo.holidayDetails!.length - 1 ? '1px dashed #fef3c7' : 'none', paddingBottom: idx < salaryInfo.holidayDetails!.length - 1 ? '4px' : '0' }}>
                        <span><strong>{dateFormatted}</strong>{detail.holidayName ? ` - ${detail.holidayName}` : ''}:</span>
                        <span>{detail.hours}h {detail.extraHours > 0 ? `(+TC ${detail.extraHours}h)` : ''} <strong>(X{detail.multiplier})</strong> &rarr; <strong style={{ color: '#b45309' }}>{formatCurrency(detail.daySalary)} đ</strong></span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Records Table */}
        <div style={{ 
          background: 'white', 
          padding: '20px', 
          borderRadius: '8px', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflowX: 'auto'
        }}>
          <h3 style={{ marginBottom: '15px', color: '#1e293b' }}>
            <i className="fas fa-list"></i> Danh sách điểm danh
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th 
                  style={{ 
                    padding: '12px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                  onClick={handleSort}
                  title={`Sắp xếp ${sortOrder === 'desc' ? 'tăng dần' : 'giảm dần'}`}
                >
                  Thời gian
                  <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                    {sortOrder === 'desc' ? '↓' : '↑'}
                  </span>
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Loại</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Địa chỉ</th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                    Không có dữ liệu điểm danh trong tháng này
                  </td>
                </tr>
              ) : (
                records.map((record) => (
                  <tr key={record.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: '12px' }}>
                      {formatDateTime(record.check_time)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: record.check_type === 'check_in' ? '#d4edda' : '#f8d7da',
                        color: record.check_type === 'check_in' ? '#155724' : '#721c24'
                      }}>
                        {record.check_type === 'check_in' ? 'IN' : 'OUT'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', maxWidth: '300px' }}>
                      <div style={{ 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'wrap',
                      }}>
                        {record.location_address.slice(0, 30)}...
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

